import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getErrorMessage = (err, fallback) => {
  if (err.response) {
    return err.response.data?.message || err.response.data?.errors?.[0]?.msg || `Server error (${err.response.status})`;
  }
  if (err.request) {
    return 'Cannot reach server. Please check your connection.';
  }
  return err.message || fallback;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('verilink_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const boot = async () => {
      // 1. Returning from Emergent Google OAuth: session_id is in the URL fragment.
      const hash = window.location.hash || '';
      if (hash.includes('session_id=')) {
        const sid = new URLSearchParams(hash.replace(/^#/, '')).get('session_id');
        window.history.replaceState({}, '', window.location.pathname);
        if (sid) {
          try {
            const { data } = await axios.post(`${API_URL}/api/auth/session`, { session_id: sid },
              { withCredentials: true, headers: { 'X-Session-ID': sid } });
            setUser(data.user);
            setToken(null); // Google sessions are cookie-based, no localStorage token
          } catch (err) {
            setError(getErrorMessage(err, 'Google sign-in failed'));
          }
          setLoading(false);
          return;
        }
      }

      // 2. Existing JWT (email/password) session
      const storedToken = localStorage.getItem('verilink_token');
      if (storedToken) {
        try {
          const { data } = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
          setUser(data.user);
          setToken(storedToken);
          setLoading(false);
          return;
        } catch (err) {
          localStorage.removeItem('verilink_token');
          setToken(null);
        }
      }

      // 3. Returning Google user with a valid session cookie
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(data.user);
      } catch (err) {
        setUser(null);
      }
      setLoading(false);
    };
    boot();
  }, []);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const register = async (email, password, name) => {
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
      localStorage.setItem('verilink_token', data.token);
      setToken(data.token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      const message = getErrorMessage(err, 'Registration failed');
      setError(message);
      return { success: false, error: message };
    }
  };

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      localStorage.setItem('verilink_token', data.token);
      setToken(data.token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      const message = getErrorMessage(err, 'Invalid email or password');
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
    } catch (err) { /* ignore network errors on logout */ }
    localStorage.removeItem('verilink_token');
    setToken(null);
    setUser(null);
    setError(null);
  }, [token]);

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const authFetch = useCallback(async (endpoint, options = {}) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios({
        url: `${API_URL}${endpoint}`,
        withCredentials: true,
        ...options,
        headers
      });
      return response;
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        throw new Error('Session expired. Please login again.');
      }
      throw err;
    }
  }, [token, logout]);

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    loginWithGoogle,
    logout,
    updateUser,
    authFetch
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
